<script lang="ts">
    import {Monitor, User} from '@lucide/svelte';
    import {
        AVAILABLE_DAYS_OPTIONS,
        CLIENT_HOURS_OPTIONS,
        DELIVERY_MODE_OPTIONS,
        ROLE_OPTIONS,
        LANGUAGE_OPTIONS,
    } from '../../form/config';
    import type {FormData, ToggleList, UpdateField} from '../../form/types';
    import Input from "../common/Input.svelte";
    import Button from "../common/Button.svelte";
    import Label from "../common/Label.svelte";
    import Checkbox from "../common/Checkbox.svelte";
    import {slide} from "svelte/transition";
    import {FormAttributesStore} from "../../form/attributes";
    import {type ValidationResult, Validator} from "../../validator/validator";
    import {Calendar, Clock} from "lucide-svelte";

    let {
        formData = $bindable(),
        valid = $bindable(), // for reading if this step is valid
        updateField,
        toggleList,
    }: {
        formData: FormData;
        updateField: UpdateField;
        toggleList: ToggleList;
        valid?: boolean;
    } = $props();


    const validator = new Validator({
        name: {required: true},
        email: {required: true, email: true},
        workPhone: {required: true, phone: true},
        birthDate: {required: true, date: true},
        address: {required: true},
        startYearOneOnOne: {required: true, number: true},
    });

    let validation = $derived(validator.validate(formData));

    $effect(() => {
        valid = validation.isValid
    })

    const readInputValue = (event: Event): string =>
        (event.currentTarget as HTMLInputElement).value;

    const qualificationId = (qualification: string): string =>
        qualification.toLowerCase().replace(/[^a-z0-9]+/g, '-');
</script>

<div class="space-y-6 animate-in fade-in duration-500"
>
    <h2 class="flex items-center gap-3 text-2xl font-bold text-slate-800">
        <User size={28} color="#7C46AF"/>
        Basisgegevens & Opleiding
    </h2>

    <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Input error={validation.errors.name} required id="full-name" placeholder="Volledige Naam"
               bind:value={formData.name}/>
        <Input error={validation.errors.email} required id="email" placeholder="E-mailadres" type="email"
               bind:value={formData.email}/>
        <Input error={validation.errors.workPhone} required id="phone" placeholder="Werk Telefoonnummer" type="tel"
               bind:value={formData.workPhone}/>
        <Input error={validation.errors.birthDate} required id="birthdate" label="Geboortedatum" type="date"
               bind:value={formData.birthDate}/>
    </div>

    <div class="space-y-1">
        <Input error={validation.errors.address} id="practice-address"
               label="Praktijkadres (Straat, Nr, Postcode, Plaats)"
               placeholder="Vul hier jouw praktijkadres in" bind:value={formData.address}/>
    </div>

    <div class="space-y-3 pt-2">
        <Label bold>
            Wat is jouw primaire rol?
        </Label>
        <div class="grid grid-cols-3 gap-3">
            {#each ROLE_OPTIONS as role}
                <Button
                        onclick={() => formData.role = role}
                        active={formData.role === role}
                        content={role}
                />
            {/each}
        </div>
    </div>

    <div class="space-y-4 pt-2">
        <div class="space-y-1">
            <Input error={validation.errors.startYearOneOnOne} id="start-year"
                   label="Welk jaar ben je gestart met 1 op 1 begeleiding?" type="number"
                   placeholder="bijv. 2015" bind:value={formData.startYearOneOnOne}/>
        </div>

        <div class="space-y-3">
            <Label bold>
                Hoofdopleidingen (selecteer en vul jaar afgerond in):
            </Label>
            <div class="custom-scrollbar grid max-h-48 grid-cols-1 gap-2 overflow-y-auto rounded-xl border bg-[#CDB6E2]/10 p-1 pr-2">
                {#each $FormAttributesStore.data.main_qualifications as qualification}
                    {@const yearId = `qualification-year-${qualificationId(qualification)}`}
                    <div
                            class={`flex flex-col gap-2 rounded-xl border p-3 transition-all ${formData.mainQualifications.includes(
              qualification,
            )
              ? 'border-blue-200 bg-white shadow-sm'
              : 'border-transparent bg-transparent'}`}
                    >
                        <Checkbox
                                checked={formData.mainQualifications.includes(qualification)}
                                onchange={() => toggleList('mainQualifications', qualification)}
                                label={qualification}
                        />
                        {#if formData.mainQualifications.includes(qualification)}
                            <div class="flex flex-col gap-2 pl-7 md:flex-row"
                                 transition:slide|local={{axis:'y', duration: 300}}
                            >
                                {#if qualification === 'Overig...'}
                                    <input
                                            type="text"
                                            placeholder="Naam opleiding"
                                            class="flex-1 rounded-lg border bg-[#CDB6E2]/10 p-2 text-[10px] outline-none "
                                            bind:value={formData.otherQualificationText}
                                    />
                                {/if}
                                <div class="flex items-center gap-2">
                                    <label for={yearId} class="shrink-0 text-[9px] font-bold uppercase text-slate-700"
                                    >Jaar afgerond:</label
                                    >
                                    <input
                                            id={yearId}
                                            type="number"
                                            placeholder="Jaar"
                                            class="w-20 rounded-lg border bg-[#CDB6E2]/10 p-2 text-[10px] outline-none "
                                            value={formData.qualificationYears[qualification] ?? ''}
                                            oninput={(event) =>
                      updateField('qualificationYears', {
                        ...formData.qualificationYears,
                        [qualification]: readInputValue(event),
                      })}
                                    />
                                </div>
                            </div>
                        {/if}
                    </div>
                {/each}
            </div>
        </div>
    </div>

    <div class="grid grid-cols-1 gap-6 pt-2 md:grid-cols-1">
        <div class="space-y-3">
            <p class="flex items-center gap-2 text-xs font-bold text-slate-700">
                <Monitor size={16} class="text-slate-400"/>
                Op welke manier vinden jouw gesprekken plaats?
            </p>
            <div class="flex gap-2">
                {#each DELIVERY_MODE_OPTIONS as mode}
                    <Button
                            size="sm"
                            onclick={() => formData.deliveryMode = mode}
                            active={formData.deliveryMode === mode}
                    >
                        {mode}
                    </Button>
                {/each}
            </div>
        </div>

        <div class="space-y-3">
            <p class="text-xs font-bold  text-slate-700 flex items-center gap-2">
                <Calendar size={16} class="text-slate-400"/>
                Welke dagen in de week ben je beschikbaar voor SpecialistenNet?
            </p>
            <div class="flex flex-wrap gap-2">
                {#each AVAILABLE_DAYS_OPTIONS as day}
                    <Button
                            size="sm"
                            onclick={() => {
                                const has = formData.availableDays.includes(day);
                                if (!has) {
                                    formData.availableDays.push(day);
                                } else {
                                    formData.availableDays = formData.availableDays.filter(d => d !== day);
                                }
                            }}
                            active={formData.availableDays.includes(day)}
                    >
                        {day}
                    </Button>
                {/each}
            </div>
        </div>

        <div class="space-y-3">
            <p class="text-xs font-bold  text-slate-700 flex items-center gap-2">
                <Clock size={16} class="text-slate-400"/>
                Hoeveel uur ben je gemiddeld per week beschikbaar voor SpecialistenNet?
            </p>
            <div class="flex flex-wrap gap-2">
                {#each CLIENT_HOURS_OPTIONS as hours}
                    <Button
                            size="sm"
                            onclick={() => formData.clientHoursRange = hours}
                            active={formData.clientHoursRange === hours}
                    >
                        {hours} uur
                    </Button>
                {/each}
            </div>
        </div>

        <div class="space-y-3">
            <p class="text-xs font-bold  text-slate-700 flex items-center gap-2">
                <Clock size={16} class="text-slate-400"/>
                In welke taal of talen kun je begeleiding bieden?
            </p>
            <div class="flex flex-wrap gap-2">
                {#each LANGUAGE_OPTIONS as language}
                    <Button
                            size="sm"
                            onclick={() => {
                                const has = formData.languages.includes(language);
                                if (!has) {
                                    formData.languages.push(language);
                                } else {
                                    formData.languages = formData.languages.filter(l => l !== language);
                                }
                            }}
                            active={formData.languages.includes(language)}
                    >
                        {language}
                    </Button>
                {/each}
            </div>
        </div>

    </div>
</div>

<style>
    @import "tailwindcss";
</style>
